<template>
  <div class="produto-analise">
    <div v-if="carregandoResumo && !resumo" class="card card-custom p-8">
      <b-skeleton width="35%" height="28px" class="mb-4" />
      <b-skeleton width="100%" height="120px" class="mb-4" />
      <b-skeleton width="100%" height="320px" />
    </div>

    <template v-else-if="resumo">
      <div class="card card-custom produto-cabecalho mb-5">
        <div class="card-body d-flex flex-wrap align-items-center">
          <button
            type="button"
            class="btn btn-light-primary btn-icon mr-4"
            v-b-tooltip.hover.top="'Voltar para produtos'"
            @click="$router.push({ name: 'produtosLista' })"
          >
            <b-icon icon="arrow-left" />
          </button>

          <div class="produto-imagem mr-5">
            <img
              v-if="produto.imagem"
              :src="produto.imagem"
              :alt="produto.descricao"
            />
            <b-icon v-else icon="box-seam" font-scale="2.4" />
          </div>

          <div class="produto-identificacao flex-grow-1 mr-4">
            <div class="d-flex flex-wrap align-items-center mb-1">
              <h2 class="mb-0 mr-3">{{ produto.descricao }}</h2>
              <span
                class="label label-lg label-inline"
                :class="produto.ativo ? 'label-light-success' : 'label-light-danger'"
              >
                {{ produto.ativo ? "Ativo" : "Inativo" }}
              </span>
            </div>
            <div class="text-muted">
              Código {{ produto.codigo || "—" }}
              <span class="mx-2">•</span>
              Referência {{ produto.referencia || "—" }}
              <span class="mx-2">•</span>
              {{ produto.unidade_medida || "Sem unidade" }}
            </div>
            <div class="text-muted mt-1">
              {{ produto.grupo || "Sem grupo" }}
              <span class="mx-2">•</span>
              {{ produto.fornecedor || "Sem fornecedor preferencial" }}
            </div>
          </div>

        </div>
      </div>

      <div class="card card-custom mb-5">
        <div class="card-body py-4">
          <div class="row align-items-end">
            <b-col md="3" sm="6">
              <b-form-group label="Data inicial" class="mb-0">
                <b-form-input v-model="filtros.data_inicial" type="date" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6">
              <b-form-group label="Data final" class="mb-0">
                <b-form-input v-model="filtros.data_final" type="date" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6">
              <b-form-group label="Agrupar gráficos" class="mb-0">
                <b-form-select v-model="filtros.agrupamento" :options="opcoesAgrupamento" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6" class="mt-3 mt-md-0">
              <button
                class="btn btn-primary btn-block"
                :disabled="carregandoResumo"
                @click="aplicarPeriodo"
              >
                <b-spinner v-if="carregandoResumo" small class="mr-2" />
                Atualizar análise
              </button>
            </b-col>
          </div>
        </div>
      </div>

      <div class="row indicadores-venda">
        <b-col
          v-for="card in cardsVenda"
          :key="card.chave"
          xl="3"
          md="6"
          class="mb-5"
        >
          <div class="card card-custom h-100 indicador-card">
            <div class="card-body">
              <div class="text-muted font-weight-bold text-uppercase mb-2">
                {{ card.titulo }}
              </div>
              <div class="font-size-h2 font-weight-bolder text-dark">
                {{ formatarQuantidade(card.dados.quantidade) }}
                <small class="text-muted">{{ produto.unidade_medida }}</small>
              </div>
              <div class="text-primary font-weight-bolder font-size-lg mt-1">
                {{ formatarMoeda(card.dados.faturamento) }}
              </div>
              <div class="text-muted font-size-sm mt-2">
                {{ card.dados.clientes }} cliente(s) identificado(s)
              </div>
            </div>
          </div>
        </b-col>
      </div>

      <div class="row mb-1">
        <b-col v-for="card in cardsEstoque" :key="card.titulo" xl="3" md="6" class="mb-5">
          <div class="card card-custom h-100 estoque-card">
            <div class="card-body d-flex align-items-center">
              <div class="estoque-card-icon mr-4" :class="card.classe">
                <b-icon :icon="card.icone" />
              </div>
              <div>
                <div class="text-muted font-weight-bold">{{ card.titulo }}</div>
                <div class="font-size-h3 font-weight-bolder text-dark">
                  {{ card.valor }}
                </div>
                <div v-if="card.legenda" class="text-muted font-size-sm">
                  {{ card.legenda }}
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </div>

      <div class="row">
        <b-col xl="8" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title font-weight-bolder">Vendas no período</h3>
            </div>
            <div class="card-body pt-2">
              <apexchart
                height="340"
                :options="opcoesGraficoVendas"
                :series="seriesGraficoVendas"
              />
              <div v-if="!temVendasNoPeriodo" class="grafico-vazio">
                Nenhuma venda encontrada no período selecionado.
              </div>
            </div>
          </div>
        </b-col>
        <b-col xl="4" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title font-weight-bolder">Origem das movimentações</h3>
            </div>
            <div class="card-body pt-2">
              <apexchart
                height="300"
                type="donut"
                :options="opcoesGraficoOrigens"
                :series="seriesGraficoOrigens"
              />
              <div v-if="!seriesGraficoOrigens.length" class="grafico-vazio">
                Nenhuma movimentação encontrada.
              </div>
            </div>
          </div>
        </b-col>
      </div>

      <div class="row">
        <b-col xl="7" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title font-weight-bolder">Histórico do estoque</h3>
            </div>
            <div class="card-body pt-2">
              <apexchart
                height="320"
                :options="opcoesGraficoEstoque"
                :series="seriesGraficoEstoque"
              />
              <div
                v-if="conciliacaoEstoque"
                class="alert alert-custom alert-light-info py-3 mt-2 mb-0"
              >
                <div class="alert-icon"><b-icon icon="info-circle" /></div>
                <div class="alert-text">
                  O saldo final foi conciliado com o estoque atual do produto
                  ({{ formatarQuantidade(produto.quantidade) }}). O histórico possuía
                  uma diferença de
                  {{ formatarQuantidade(conciliacaoEstoque.diferenca_conciliacao) }}.
                </div>
              </div>
            </div>
          </div>
        </b-col>
        <b-col xl="5" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5 d-flex align-items-center">
              <h3 class="card-title font-weight-bolder mb-0">10 clientes que mais compraram</h3>
              <b-form-select
                v-model="metricaClientes"
                size="sm"
                class="ml-auto seletor-metrica"
                :options="opcoesMetricaClientes"
              />
            </div>
            <div class="card-body pt-2">
              <apexchart
                height="320"
                type="bar"
                :options="opcoesGraficoClientes"
                :series="seriesGraficoClientes"
              />
              <div v-if="!clientes.length" class="grafico-vazio">
                Nenhum cliente identificado no período.
              </div>
            </div>
          </div>
        </b-col>
      </div>

      <div class="row">
        <b-col xl="6" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title font-weight-bolder">Informações comerciais</h3>
            </div>
            <div class="card-body pt-2">
              <div class="info-grid">
                <div class="info-item">
                  <span>Preço atual</span>
                  <strong>{{ formatarMoeda(produto.preco) }}</strong>
                </div>
                <div class="info-item">
                  <span>Custo atual</span>
                  <strong>{{ formatarMoeda(produto.custo) }}</strong>
                </div>
                <div class="info-item">
                  <span>Margem atual</span>
                  <strong>
                    {{ formatarMoeda(produto.margem_valor) }}
                    ({{ formatarPercentual(produto.margem_percentual) }})
                  </strong>
                </div>
                <div class="info-item">
                  <span>Preço médio no período</span>
                  <strong>{{ formatarMoeda(indicadores.periodo.preco_medio) }}</strong>
                </div>
                <div class="info-item">
                  <span>Última venda</span>
                  <strong>{{ formatarDataHora(indicadores.ultima_venda?.data) }}</strong>
                  <small v-if="indicadores.ultima_venda?.pessoa">
                    {{ indicadores.ultima_venda.pessoa }}
                  </small>
                </div>
                <div class="info-item">
                  <span>Última compra</span>
                  <strong>{{ formatarDataHora(indicadores.ultima_compra?.data) }}</strong>
                  <small v-if="indicadores.ultima_compra?.pessoa">
                    {{ indicadores.ultima_compra.pessoa }}
                  </small>
                </div>
                <div class="info-item">
                  <span>Dias sem venda</span>
                  <strong>{{ indicadores.dias_sem_venda ?? "Sem histórico" }}</strong>
                </div>
                <div class="info-item">
                  <span>Média diária (30 dias)</span>
                  <strong>{{ formatarQuantidade(indicadores.media_diaria_30_dias) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col xl="6" class="mb-5">
          <div class="card card-custom h-100">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title font-weight-bolder">Ranking de clientes</h3>
            </div>
            <div class="card-body pt-2 px-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cliente</th>
                      <th class="text-right">Quantidade</th>
                      <th class="text-right">Faturamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(cliente, index) in clientes" :key="cliente.id_pessoa">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <strong>{{ cliente.nome }}</strong>
                        <small class="d-block text-muted">
                          Última compra: {{ formatarDataHora(cliente.ultima_compra) }}
                        </small>
                      </td>
                      <td class="text-right">{{ formatarQuantidade(cliente.quantidade) }}</td>
                      <td class="text-right">{{ formatarMoeda(cliente.faturamento) }}</td>
                    </tr>
                    <tr v-if="!clientes.length">
                      <td colspan="4" class="text-center text-muted py-5">
                        Nenhum cliente identificado.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </b-col>
      </div>

      <div v-if="produto.variavel" class="card card-custom mb-5">
        <div class="card-header border-0 pt-5">
          <div>
            <h3 class="card-title font-weight-bolder mb-1">
              Análise por variação
            </h3>
            <div class="text-muted">
              Disponibilidade e desempenho de cada combinação no período.
            </div>
          </div>
        </div>
        <div class="card-body pt-3">
          <div
            v-if="resumoVariacoes && resumoVariacoes.divergencia_estoque !== 0"
            class="alert alert-custom alert-light-warning mb-5"
          >
            <div class="alert-icon"><b-icon icon="exclamation-triangle" /></div>
            <div class="alert-text">
              A soma das variações diverge do estoque geral em
              <strong>{{ formatarQuantidade(resumoVariacoes.divergencia_estoque) }}</strong>.
            </div>
          </div>

          <div class="row">
            <b-col
              v-for="card in cardsResumoVariacoes"
              :key="card.titulo"
              xl="3"
              md="6"
              class="mb-4"
            >
              <div class="especial-resumo-card" :class="card.classe">
                <span>{{ card.titulo }}</span>
                <strong>{{ card.valor }}</strong>
                <small>{{ card.legenda }}</small>
              </div>
            </b-col>
          </div>

          <div v-if="variacoes.length" class="row align-items-stretch">
            <b-col xl="5" class="mb-4 mb-xl-0">
              <div class="painel-especial-grafico">
                <h5>Estoque atual x vendido no período</h5>
                <apexchart
                  type="bar"
                  :height="alturaGraficoVariacoes"
                  :options="opcoesGraficoVariacoes"
                  :series="seriesGraficoVariacoes"
                />
              </div>
            </b-col>
            <b-col xl="7">
              <b-table
                small
                responsive
                hover
                :items="variacoes"
                :fields="camposVariacoes"
              >
                <template #cell(status)="row">
                  <span class="label label-inline" :class="classeStatusVariacao(row.item.status)">
                    {{ descricaoStatusVariacao(row.item.status) }}
                  </span>
                </template>
                <template #cell(quantidade)="row">
                  <strong>{{ formatarQuantidade(row.item.quantidade) }}</strong>
                </template>
                <template #cell(percentual_estoque)="row">
                  {{ formatarPercentual(row.item.percentual_estoque) }}
                </template>
                <template #cell(vendido_periodo)="row">
                  {{ formatarQuantidade(row.item.vendido_periodo) }}
                </template>
                <template #cell(faturamento_periodo)="row">
                  {{ formatarMoeda(row.item.faturamento_periodo) }}
                </template>
                <template #cell(ultima_movimentacao_periodo)="row">
                  {{ formatarDataHora(row.item.ultima_movimentacao_periodo) }}
                </template>
              </b-table>
            </b-col>
          </div>
          <div v-else class="text-center text-muted py-8">
            Nenhuma combinação cadastrada para este produto.
          </div>
        </div>
      </div>

      <div v-if="produto.lote" class="card card-custom mb-5">
        <div class="card-header border-0 pt-5">
          <div>
            <h3 class="card-title font-weight-bolder mb-1">Análise por lote</h3>
            <div class="text-muted">
              Saldos, vencimentos e desempenho individual dos lotes.
            </div>
          </div>
        </div>
        <div class="card-body pt-3">
          <div
            v-if="resumoLotes && resumoLotes.divergencia_estoque !== 0"
            class="alert alert-custom alert-light-warning mb-5"
          >
            <div class="alert-icon"><b-icon icon="exclamation-triangle" /></div>
            <div class="alert-text">
              A soma dos lotes diverge do estoque geral em
              <strong>{{ formatarQuantidade(resumoLotes.divergencia_estoque) }}</strong>.
            </div>
          </div>

          <div class="row">
            <b-col
              v-for="card in cardsResumoLotes"
              :key="card.titulo"
              xl="3"
              md="6"
              class="mb-4"
            >
              <div class="especial-resumo-card" :class="card.classe">
                <span>{{ card.titulo }}</span>
                <strong>{{ card.valor }}</strong>
                <small>{{ card.legenda }}</small>
              </div>
            </b-col>
          </div>

          <div v-if="lotes.length" class="painel-especial-grafico mb-5">
            <h5>Distribuição do estoque por lote</h5>
            <apexchart
              type="bar"
              height="280"
              :options="opcoesGraficoLotes"
              :series="seriesGraficoLotes"
            />
          </div>

          <b-table
            v-if="lotes.length"
            small
            responsive
            hover
            :items="lotes"
            :fields="camposLotes"
          >
            <template #cell(status)="row">
              <span class="label label-inline" :class="classeStatusLote(row.item.status)">
                {{ descricaoStatusLote(row.item.status) }}
              </span>
            </template>
            <template #cell(fabricacao)="row">
              {{ formatarData(row.item.fabricacao) }}
            </template>
            <template #cell(validade)="row">
              <strong>{{ formatarData(row.item.validade) }}</strong>
              <small class="d-block text-muted">{{ descricaoVencimento(row.item) }}</small>
            </template>
            <template #cell(quantidade)="row">
              <strong>{{ formatarQuantidade(row.item.quantidade) }}</strong>
            </template>
            <template #cell(percentual_estoque)="row">
              {{ formatarPercentual(row.item.percentual_estoque) }}
            </template>
            <template #cell(vendido_periodo)="row">
              {{ formatarQuantidade(row.item.vendido_periodo) }}
            </template>
            <template #cell(faturamento_periodo)="row">
              {{ formatarMoeda(row.item.faturamento_periodo) }}
            </template>
            <template #cell(ultima_movimentacao_periodo)="row">
              {{ formatarDataHora(row.item.ultima_movimentacao_periodo) }}
            </template>
          </b-table>
          <div v-else class="text-center text-muted py-8">
            Nenhum lote cadastrado para este produto.
          </div>
        </div>
      </div>

      <div v-if="composicao.length" class="card card-custom mb-5">
        <div class="card-header border-0 pt-5">
          <h3 class="card-title font-weight-bolder">Composição do produto</h3>
        </div>
        <div class="card-body pt-2">
          <b-table
            small
            responsive
            :items="composicao"
            :fields="camposComposicao"
          >
            <template #cell(quantidade)="row">
              {{ formatarQuantidade(row.item.quantidade) }}
            </template>
          </b-table>
        </div>
      </div>

      <div class="card card-custom mb-5">
        <div class="card-header border-0 pt-5">
          <div>
            <h3 class="card-title font-weight-bolder mb-1">Movimentações detalhadas</h3>
            <div class="text-muted">Entradas, saídas, vendas, compras, ajustes e cancelamentos.</div>
          </div>
        </div>
        <div class="card-body pt-3">
          <div class="row align-items-end mb-5">
            <b-col lg="4" md="6">
              <b-form-group label="Buscar cliente, documento, lote ou variação" class="mb-0">
                <b-form-input
                  v-model="filtrosMovimentacao.busca"
                  placeholder="Digite para pesquisar"
                  @keyup.enter="buscarMovimentacoes(1)"
                />
              </b-form-group>
            </b-col>
            <b-col lg="4" md="6">
              <b-form-group label="Tipos de movimentação" class="mb-0">
                <VueSelect
                  v-model="filtrosMovimentacao.origens"
                  :options="opcoesOrigem"
                  :reduce="(opcao) => opcao.value"
                  label="text"
                  multiple
                  placeholder="Todos os tipos"
                />
              </b-form-group>
            </b-col>
            <b-col lg="2" md="6" class="mt-3 mt-lg-0">
              <b-form-group label="Itens por página" class="mb-0">
                <b-form-select
                  v-model="filtrosMovimentacao.per_page"
                  :options="[25, 50, 100]"
                  @change="buscarMovimentacoes(1)"
                />
              </b-form-group>
            </b-col>
            <b-col lg="2" md="6" class="mt-3 mt-lg-0">
              <button
                class="btn btn-primary btn-block"
                :disabled="carregandoMovimentacoes"
                @click="buscarMovimentacoes(1)"
              >
                Filtrar
              </button>
            </b-col>
          </div>

          <b-table
            responsive
            hover
            small
            show-empty
            empty-text="Nenhuma movimentação encontrada."
            :busy="carregandoMovimentacoes"
            :items="movimentacoes"
            :fields="camposMovimentacoes"
          >
            <template #table-busy>
              <div class="text-center text-primary py-5">
                <b-spinner class="mr-2" /> Carregando movimentações...
              </div>
            </template>
            <template #cell(data)="row">
              <span class="text-nowrap">{{ formatarDataHora(row.item.data) }}</span>
            </template>
            <template #cell(tipo)="row">
              <span class="label label-inline" :class="classeTipo(row.item.tipo)">
                {{ descricaoTipo(row.item.tipo) }}
              </span>
            </template>
            <template #cell(quantidade)="row">
              <strong>{{ formatarImpacto(row.item) }}</strong>
            </template>
            <template #cell(saldo)="row">
              {{ row.item.saldo === null ? "—" : formatarQuantidade(row.item.saldo) }}
            </template>
            <template #cell(pessoa)="row">
              {{ row.item.pessoa || "—" }}
            </template>
            <template #cell(documento)="row">
              {{ row.item.documento || "—" }}
            </template>
            <template #cell(valor_total)="row">
              {{ formatarMoeda(row.item.valor_total) }}
            </template>
            <template #cell(detalhes)="row">
              <span v-if="row.item.combinacao" class="d-block">
                Variação: {{ row.item.combinacao }}
              </span>
              <span v-if="row.item.lote" class="d-block">
                Lote: {{ row.item.lote }}
              </span>
              <span v-if="!row.item.combinacao && !row.item.lote">—</span>
            </template>
          </b-table>

          <div class="d-flex flex-wrap align-items-center justify-content-between mt-4">
            <div class="text-muted mb-2">
              {{ paginacao.total }} movimentação(ões) encontrada(s)
            </div>
            <b-pagination
              v-model="paginacao.current_page"
              :total-rows="paginacao.total"
              :per-page="paginacao.per_page"
              @input="buscarMovimentacoes"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "app-produto-movimentacoes",
  components: { VueSelect },
  data() {
    const dataFinal = this.dataLocal(new Date());
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 29);

    return {
      resumo: null,
      carregandoResumo: false,
      carregandoMovimentacoes: false,
      metricaClientes: "faturamento",
      filtros: {
        data_inicial: this.dataLocal(inicio),
        data_final: dataFinal,
        agrupamento: "auto",
      },
      filtrosMovimentacao: {
        busca: "",
        origens: [],
        per_page: 25,
      },
      movimentacoes: [],
      paginacao: {
        current_page: 1,
        per_page: 25,
        total: 0,
      },
      opcoesAgrupamento: [
        { value: "auto", text: "Automático" },
        { value: "dia", text: "Por dia" },
        { value: "semana", text: "Por semana" },
        { value: "mes", text: "Por mês" },
      ],
      opcoesMetricaClientes: [
        { value: "faturamento", text: "Faturamento" },
        { value: "quantidade", text: "Quantidade" },
      ],
      camposMovimentacoes: [
        { key: "data", label: "Data" },
        { key: "origem_descricao", label: "Movimentação" },
        { key: "tipo", label: "Tipo" },
        { key: "quantidade", label: "Quantidade", class: "text-right" },
        { key: "saldo", label: "Saldo", class: "text-right" },
        { key: "pessoa", label: "Cliente/Fornecedor" },
        { key: "documento", label: "Documento" },
        { key: "valor_total", label: "Valor", class: "text-right" },
        { key: "detalhes", label: "Detalhes" },
      ],
      camposLotes: [
        { key: "numero", label: "Lote" },
        { key: "status", label: "Situação" },
        { key: "fabricacao", label: "Fabricação" },
        { key: "validade", label: "Validade" },
        { key: "quantidade", label: "Estoque", class: "text-right" },
        { key: "percentual_estoque", label: "% estoque", class: "text-right" },
        { key: "vendido_periodo", label: "Vendido", class: "text-right" },
        {
          key: "faturamento_periodo",
          label: "Faturamento",
          class: "text-right",
        },
        { key: "ultima_movimentacao_periodo", label: "Última movimentação" },
      ],
      camposVariacoes: [
        { key: "descricao", label: "Variação" },
        { key: "status", label: "Situação" },
        { key: "quantidade", label: "Estoque", class: "text-right" },
        { key: "percentual_estoque", label: "% estoque", class: "text-right" },
        { key: "vendido_periodo", label: "Vendido", class: "text-right" },
        {
          key: "faturamento_periodo",
          label: "Faturamento",
          class: "text-right",
        },
        { key: "ultima_movimentacao_periodo", label: "Última movimentação" },
      ],
      camposComposicao: [
        { key: "codigo", label: "Código" },
        { key: "descricao", label: "Componente" },
        { key: "quantidade", label: "Quantidade", class: "text-right" },
      ],
    };
  },
  computed: {
    produto() {
      return this.resumo?.produto || {};
    },
    indicadores() {
      return this.resumo?.indicadores || {
        hoje: {},
        ultimos_7_dias: {},
        ultimos_30_dias: {},
        total: {},
        periodo: {},
      };
    },
    clientes() {
      return this.resumo?.clientes || [];
    },
    lotes() {
      return this.resumo?.lotes || [];
    },
    variacoes() {
      return this.resumo?.variacoes || [];
    },
    resumoVariacoes() {
      return this.resumo?.resumo_variacoes || null;
    },
    resumoLotes() {
      return this.resumo?.resumo_lotes || null;
    },
    composicao() {
      return this.resumo?.composicao || [];
    },
    opcoesOrigem() {
      return this.resumo?.opcoes_origem || [];
    },
    cardsResumoVariacoes() {
      const dados = this.resumoVariacoes || {};
      const maisVendida = dados.mais_vendida_periodo;

      return [
        {
          titulo: "Combinações",
          valor: dados.total_variacoes || 0,
          legenda: `${dados.com_estoque || 0} com estoque disponível`,
          classe: "especial-primary",
        },
        {
          titulo: "Estoque nas variações",
          valor: this.formatarQuantidade(dados.quantidade_total),
          legenda: "Soma de todas as combinações",
          classe: "especial-success",
        },
        {
          titulo: "Sem estoque ou negativo",
          valor: Number(dados.sem_estoque || 0) + Number(dados.estoque_negativo || 0),
          legenda: `${dados.estoque_negativo || 0} com saldo negativo`,
          classe: "especial-danger",
        },
        {
          titulo: "Mais vendida no período",
          valor: maisVendida?.descricao || "Sem vendas",
          legenda: maisVendida
            ? `${this.formatarQuantidade(maisVendida.quantidade)} vendida(s)`
            : "Nenhuma combinação identificada",
          classe: "especial-info",
        },
      ];
    },
    cardsResumoLotes() {
      const dados = this.resumoLotes || {};
      const proximo = dados.proximo_vencimento;

      return [
        {
          titulo: "Lotes cadastrados",
          valor: dados.total_lotes || 0,
          legenda: `${dados.com_estoque || 0} com estoque disponível`,
          classe: "especial-primary",
        },
        {
          titulo: "Estoque nos lotes",
          valor: this.formatarQuantidade(dados.quantidade_total),
          legenda: "Soma de todos os lotes",
          classe: "especial-success",
        },
        {
          titulo: "Vencidos / próximos 30 dias",
          valor: `${dados.vencidos_com_estoque || 0} / ${dados.a_vencer_30_dias || 0}`,
          legenda: `${this.formatarQuantidade(dados.quantidade_vencida)} unidade(s) vencida(s)`,
          classe: "especial-danger",
        },
        {
          titulo: "Próximo vencimento",
          valor: proximo ? this.formatarData(proximo.validade) : "Sem previsão",
          legenda: proximo
            ? `Lote ${proximo.numero} · ${proximo.dias} dia(s)`
            : "Nenhum lote disponível com validade",
          classe: "especial-warning",
        },
      ];
    },
    alturaGraficoVariacoes() {
      return Math.max(280, this.variacoes.length * 42);
    },
    seriesGraficoVariacoes() {
      return [
        {
          name: "Estoque atual",
          data: this.variacoes.map((item) => Number(item.quantidade || 0)),
        },
        {
          name: "Vendido no período",
          data: this.variacoes.map((item) => Number(item.vendido_periodo || 0)),
        },
      ];
    },
    opcoesGraficoVariacoes() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
        colors: ["#3699ff", "#1bc5bd"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.variacoes.map((item) => item.descricao),
          labels: { formatter: (valor) => this.formatarNumero(valor, 0) },
        },
        tooltip: {
          y: { formatter: (valor) => this.formatarQuantidade(valor) },
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoLotes() {
      return [
        {
          name: "Estoque atual",
          data: this.lotes.map((item) => Number(item.quantidade || 0)),
        },
        {
          name: "Vendido no período",
          data: this.lotes.map((item) => Number(item.vendido_periodo || 0)),
        },
      ];
    },
    opcoesGraficoLotes() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "48%" } },
        colors: ["#8950fc", "#1bc5bd"],
        dataLabels: { enabled: false },
        xaxis: { categories: this.lotes.map((item) => item.numero) },
        yaxis: { labels: { formatter: (valor) => this.formatarNumero(valor, 0) } },
        tooltip: {
          y: { formatter: (valor) => this.formatarQuantidade(valor) },
        },
        legend: { position: "top" },
      };
    },
    cardsVenda() {
      return [
        { chave: "hoje", titulo: "Vendido hoje", dados: this.indicadores.hoje },
        { chave: "semana", titulo: "Últimos 7 dias", dados: this.indicadores.ultimos_7_dias },
        { chave: "mes", titulo: "Últimos 30 dias", dados: this.indicadores.ultimos_30_dias },
        { chave: "total", titulo: "Desde o início", dados: this.indicadores.total },
      ];
    },
    cardsEstoque() {
      const cobertura = this.indicadores.dias_cobertura;

      return [
        {
          titulo: "Estoque atual",
          valor: this.formatarQuantidade(this.produto.quantidade),
          legenda: `Mínimo: ${this.formatarQuantidade(this.produto.estoque_minimo)}`,
          icone: "box-seam",
          classe: "bg-light-primary text-primary",
        },
        {
          titulo: "Estoque disponível",
          valor: this.formatarQuantidade(this.produto.estoque_disponivel),
          legenda: `Reservado: ${this.formatarQuantidade(this.produto.estoque_reservado)}`,
          icone: "check2-circle",
          classe: "bg-light-success text-success",
        },
        {
          titulo: "Valor em estoque",
          valor: this.formatarMoeda(this.produto.valor_estoque),
          legenda: "Calculado pelo custo atual",
          icone: "cash-stack",
          classe: "bg-light-info text-info",
        },
        {
          titulo: "Cobertura estimada",
          valor: cobertura === null ? "Sem previsão" : `${this.formatarNumero(cobertura, 1)} dias`,
          legenda: "Baseada nos últimos 30 dias",
          icone: "calendar-check",
          classe: "bg-light-warning text-warning",
        },
      ];
    },
    temVendasNoPeriodo() {
      return (this.resumo?.vendas || []).some(
        (item) => Number(item.quantidade) !== 0 || Number(item.faturamento) !== 0
      );
    },
    categoriasVendas() {
      return (this.resumo?.vendas || []).map((item) => this.formatarPeriodo(item.periodo));
    },
    seriesGraficoVendas() {
      const vendas = this.resumo?.vendas || [];
      return [
        {
          name: "Quantidade",
          type: "column",
          data: vendas.map((item) => Number(item.quantidade || 0)),
        },
        {
          name: "Faturamento",
          type: "line",
          data: vendas.map((item) => Number(item.faturamento || 0)),
        },
      ];
    },
    opcoesGraficoVendas() {
      return {
        chart: { toolbar: { show: true }, zoom: { enabled: true } },
        colors: ["#3699ff", "#1bc5bd"],
        stroke: { width: [0, 3], curve: "smooth" },
        dataLabels: { enabled: false },
        xaxis: { categories: this.categoriasVendas },
        yaxis: [
          {
            title: { text: "Quantidade" },
            labels: { formatter: (valor) => this.formatarNumero(valor, 0) },
          },
          {
            opposite: true,
            title: { text: "Faturamento" },
            labels: { formatter: (valor) => this.formatarMoedaCompacta(valor) },
          },
        ],
        tooltip: {
          shared: true,
          y: [
            { formatter: (valor) => this.formatarQuantidade(valor) },
            { formatter: (valor) => this.formatarMoeda(valor) },
          ],
        },
        legend: { position: "top" },
      };
    },
    seriesGraficoEstoque() {
      return [
        {
          name: "Saldo do estoque",
          data: (this.resumo?.estoque || []).map((item) => Number(item.saldo || 0)),
        },
      ];
    },
    conciliacaoEstoque() {
      return (this.resumo?.estoque || []).find(
        (item) => item.conciliado_estoque_atual
      );
    },
    opcoesGraficoEstoque() {
      return {
        chart: { type: "area", toolbar: { show: true }, zoom: { enabled: true } },
        colors: ["#8950fc"],
        stroke: { curve: "smooth", width: 3 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.resumo?.estoque || []).map((item) =>
            this.formatarPeriodo(item.periodo)
          ),
        },
        yaxis: { labels: { formatter: (valor) => this.formatarNumero(valor, 2) } },
        tooltip: { y: { formatter: (valor) => this.formatarQuantidade(valor) } },
      };
    },
    seriesGraficoOrigens() {
      return (this.resumo?.origens || [])
        .filter((item) => Number(item.quantidade) > 0)
        .map((item) => Number(item.quantidade));
    },
    opcoesGraficoOrigens() {
      const origens = (this.resumo?.origens || []).filter(
        (item) => Number(item.quantidade) > 0
      );
      return {
        labels: origens.map((item) => item.descricao),
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        tooltip: { y: { formatter: (valor) => this.formatarQuantidade(valor) } },
        colors: ["#3699ff", "#1bc5bd", "#f64e60", "#ffa800", "#8950fc", "#6993ff"],
      };
    },
    seriesGraficoClientes() {
      return [
        {
          name: this.metricaClientes === "faturamento" ? "Faturamento" : "Quantidade",
          data: this.clientes.map((cliente) => Number(cliente[this.metricaClientes] || 0)),
        },
      ];
    },
    opcoesGraficoClientes() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#1bc5bd"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.clientes.map((cliente) => cliente.nome),
          labels: {
            formatter: (valor) =>
              this.metricaClientes === "faturamento"
                ? this.formatarMoedaCompacta(valor)
                : this.formatarNumero(valor, 0),
          },
        },
        tooltip: {
          y: {
            formatter: (valor) =>
              this.metricaClientes === "faturamento"
                ? this.formatarMoeda(valor)
                : this.formatarQuantidade(valor),
          },
        },
      };
    },
  },
  async mounted() {
    await this.carregarResumo();
    await this.buscarMovimentacoes(1);
  },
  methods: {
    dataLocal(data) {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const dia = String(data.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    },
    async carregarResumo() {
      this.carregandoResumo = true;
      try {
        const { data } = await this.$http.post("/produtos/analise", {
          id_produto: this.$route.params.id_produto,
          ...this.filtros,
        });
        this.resumo = data?.data || null;
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || "Não foi possível carregar a análise do produto."
        );
        this.$router.push({ name: "produtosLista" });
      }
      this.carregandoResumo = false;
    },
    async aplicarPeriodo() {
      if (!this.filtros.data_inicial || !this.filtros.data_final) {
        this.$toast.warning("Informe o período completo.");
        return;
      }
      if (this.filtros.data_inicial > this.filtros.data_final) {
        this.$toast.warning("A data inicial não pode ser maior que a data final.");
        return;
      }

      await this.carregarResumo();
      await this.buscarMovimentacoes(1);
    },
    async buscarMovimentacoes(page = 1) {
      if (!this.resumo) return;
      this.carregandoMovimentacoes = true;
      try {
        const { data } = await this.$http.post("/produtos/analise/movimentacoes", {
          id_produto: this.$route.params.id_produto,
          data_inicial: this.filtros.data_inicial,
          data_final: this.filtros.data_final,
          busca: this.filtrosMovimentacao.busca || null,
          origens: this.filtrosMovimentacao.origens,
          per_page: Number(this.filtrosMovimentacao.per_page),
          page,
        });
        const resultado = data?.data || {};
        this.movimentacoes = resultado.data || [];
        this.paginacao = {
          current_page: Number(resultado.current_page || 1),
          per_page: Number(resultado.per_page || this.filtrosMovimentacao.per_page),
          total: Number(resultado.total || 0),
        };
      } catch (error) {
        this.$toast.error("Não foi possível carregar as movimentações do produto.");
      }
      this.carregandoMovimentacoes = false;
    },
    formatarNumero(valor, casas = 2) {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      }).format(Number(valor || 0));
    },
    formatarQuantidade(valor) {
      return this.formatarNumero(valor, 3);
    },
    formatarMoeda(valor) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(valor || 0));
    },
    formatarMoedaCompacta(valor) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(Number(valor || 0));
    },
    formatarPercentual(valor) {
      return `${this.formatarNumero(valor, 2)}%`;
    },
    formatarData(valor) {
      if (!valor) return "—";
      return new Intl.DateTimeFormat("pt-BR").format(new Date(`${valor}T00:00:00`));
    },
    formatarDataHora(valor) {
      if (!valor) return "—";
      const data = typeof valor === "string" ? valor.replace(" ", "T") : valor;
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(data));
    },
    formatarPeriodo(valor) {
      if (!valor) return "";
      const [ano, mes, dia] = valor.split("-");
      if (this.resumo?.periodo?.agrupamento === "mes") return `${mes}/${ano}`;
      return `${dia}/${mes}`;
    },
    formatarImpacto(item) {
      if (item.impacto_estoque === null) {
        return `= ${this.formatarQuantidade(item.quantidade)}`;
      }
      const sinal = Number(item.impacto_estoque) > 0 ? "+" : "";
      return `${sinal}${this.formatarQuantidade(item.impacto_estoque)}`;
    },
    classeTipo(tipo) {
      if (tipo === "entrada") return "label-light-success";
      if (tipo === "saida") return "label-light-danger";
      if (tipo === "ajuste") return "label-light-primary";
      return "label-light-secondary";
    },
    descricaoTipo(tipo) {
      if (tipo === "entrada") return "Entrada";
      if (tipo === "saida") return "Saída";
      if (tipo === "ajuste") return "Ajuste";
      return "Informativo";
    },
    classeStatusVariacao(status) {
      if (status === "disponivel") return "label-light-success";
      if (status === "negativo") return "label-light-danger";
      return "label-light-warning";
    },
    descricaoStatusVariacao(status) {
      if (status === "disponivel") return "Disponível";
      if (status === "negativo") return "Estoque negativo";
      return "Sem estoque";
    },
    classeStatusLote(status) {
      if (status === "disponivel") return "label-light-success";
      if (status === "vencido") return "label-light-danger";
      if (status === "a_vencer") return "label-light-warning";
      return "label-light-secondary";
    },
    descricaoStatusLote(status) {
      if (status === "disponivel") return "Disponível";
      if (status === "vencido") return "Vencido";
      if (status === "a_vencer") return "Vence em até 30 dias";
      return "Sem estoque";
    },
    descricaoVencimento(lote) {
      if (lote.dias_para_vencer === null) return "Sem data de validade";
      if (lote.dias_para_vencer < 0) {
        return `Vencido há ${Math.abs(lote.dias_para_vencer)} dia(s)`;
      }
      if (lote.dias_para_vencer === 0) return "Vence hoje";
      return `Vence em ${lote.dias_para_vencer} dia(s)`;
    },
  },
};
</script>

<style scoped>
.produto-analise {
  padding-bottom: 24px;
}

.produto-cabecalho .card-body {
  min-height: 138px;
}

.produto-imagem {
  width: 76px;
  height: 76px;
  border-radius: 14px;
  background: #f3f6f9;
  color: #7e8299;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.produto-imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.indicador-card,
.estoque-card {
  border: 1px solid #eef0f6;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.indicador-card:hover,
.estoque-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.08);
}

.estoque-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.grafico-vazio {
  text-align: center;
  color: #7e8299;
  padding: 20px 0;
}

.seletor-metrica {
  width: 145px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.info-item {
  background: #f8f9fb;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
}

.info-item span,
.info-item small {
  color: #7e8299;
}

.info-item strong {
  color: #181c32;
  margin-top: 4px;
}

.especial-resumo-card {
  min-height: 118px;
  padding: 18px;
  border-radius: 12px;
  border-left: 4px solid #3699ff;
  background: #f3f6f9;
  display: flex;
  flex-direction: column;
}

.especial-resumo-card span,
.especial-resumo-card small {
  color: #7e8299;
}

.especial-resumo-card strong {
  color: #181c32;
  font-size: 1.35rem;
  margin: 5px 0;
}

.especial-resumo-card.especial-success {
  border-left-color: #1bc5bd;
}

.especial-resumo-card.especial-danger {
  border-left-color: #f64e60;
}

.especial-resumo-card.especial-info {
  border-left-color: #6993ff;
}

.especial-resumo-card.especial-warning {
  border-left-color: #ffa800;
}

.painel-especial-grafico {
  height: 100%;
  padding: 16px;
  border: 1px solid #eef0f6;
  border-radius: 12px;
}

.painel-especial-grafico h5 {
  color: #3f4254;
  font-weight: 600;
  margin-bottom: 8px;
}

@media (max-width: 767px) {
  .produto-cabecalho .card-body {
    align-items: flex-start !important;
  }

  .produto-identificacao {
    min-width: calc(100% - 120px);
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
